import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getFilterByKey} from 'search/FilterUtil';
import * as FilterUtil from 'search/FilterUtil';
import {
  Filter,
  FilterKey,
  MultiValueFilter,
  Operator,
  SearchParams,
  keyToEmptyFilter,
} from 'search/SearchConstants';
import EntityStore from 'stores/EntityStore';
import APIUtil from 'util/APIUtil';

import {ProjectAttributeFilterValue} from './ProjectListingConstants';
import {MY_PROJECTS, SHARED_WITH_ME} from './ProjectListingUtil';

const COMPOSITE_FILTER_KEYS = [FilterKey.PROJECT_ATTRIBUTE, FilterKey.SHARED_WITH];

function translateSharedWith(filter: Filter | null): Array<Filter> {
  // No filter or SharedWith:All
  if (!filter || filter.value == null) {
    return [];
  }
  const currentUserId = EntityStore.currentUser().id;
  assert(typeof filter.value === 'string', 'Invalid value');
  const sharedWithValue = filter.value;
  const typeFromId = APIUtil.apiIdToMaybeType(sharedWithValue);

  if (sharedWithValue === MY_PROJECTS) {
    return [
      {
        key: FilterKey.OWNER,
        operator: Operator.IS_ONE_OF,
        value: [currentUserId],
      },
    ];
  }

  if (sharedWithValue === SHARED_WITH_ME) {
    return [
      {
        key: FilterKey.OWNER,
        operator: Operator.IS_NONE_OF,
        value: [currentUserId],
      },
      {
        key: FilterKey.OWNER_OR_COLLABORATOR,
        operator: Operator.IS_ONE_OF,
        value: [currentUserId],
      },
    ];
  }

  if (typeFromId === ModelTypeConstants.TEAM) {
    return [
      {
        key: FilterKey.OWNER_OR_COLLABORATOR,
        operator: Operator.IS_ONE_OF,
        value: [sharedWithValue],
      },
    ];
  }

  if (typeFromId === ModelTypeConstants.ENTITY) {
    return [
      {
        key: FilterKey.OWNER_OR_COLLABORATOR,
        operator: Operator.IS_ONE_OF,
        value: [sharedWithValue],
      },
    ];
  }

  return [];
}

function translateAttribute(filter: Filter | null): Array<Filter> {
  // No filter or Attribute:All
  if (!filter || filter.value == null) {
    return [
      {
        key: FilterKey.HIDDEN,
        operator: Operator.IS,
        value: 'not_hidden',
      },
    ];
  }
  assert(typeof filter.value === 'string', 'Invalid value');
  const attributeValue = filter.value;

  // The new default attribute filter value is null (handled above) but we still need to handle this legacy
  // default value since many users have this set. After the new listing is fully released we can delete all
  // project attribute filter preferences with this value and remove this case.
  if (attributeValue === ProjectAttributeFilterValue.ALL_ACTIVE) {
    return [
      {
        key: FilterKey.HIDDEN,
        operator: Operator.IS,
        value: 'not_hidden',
      },
    ];
  }

  if (attributeValue === ProjectAttributeFilterValue.STARRED) {
    return [
      {
        key: FilterKey.STARRED,
        operator: Operator.IS,
        value: 'starred',
      },
    ];
  }

  if (attributeValue === ProjectAttributeFilterValue.HIDDEN) {
    return [
      {
        key: FilterKey.HIDDEN,
        operator: Operator.IS,
        value: 'hidden',
      },
    ];
  }

  if (attributeValue === ProjectAttributeFilterValue.ARCHIVED) {
    return [
      {
        key: FilterKey.ARCHIVE_PURPOSES,
        operator: Operator.IS_ONE_OF,
        value: BenchlingConfig.ARCHIVE_NON_EXPENDABLE_PURPOSES,
      },
    ];
  }

  return [];
}

export function translateCompositeToSightglass(filters: Array<Filter>): Array<Filter> {
  const otherFilters = filters.filter((filter) => !COMPOSITE_FILTER_KEYS.includes(filter.key));

  const sharedWithSGFilters = translateSharedWith(getFilterByKey(filters, FilterKey.SHARED_WITH));
  const attributeSGFilters = translateAttribute(getFilterByKey(filters, FilterKey.PROJECT_ATTRIBUTE));

  const translatedFilters = [...sharedWithSGFilters, ...attributeSGFilters];

  assert(
    translatedFilters.map((f) => f.key).every((key) => !otherFilters.map((f) => f.key).includes(key)),
    'Generated filter conflicts with existing filter'
  );

  return [...otherFilters, ...translatedFilters];
}

/**
 * Ensure that the params specified in a projects listing URL match the expected params based
 * on the PERMISSIONS_ENABLE_GRANULAR_FOLDER_PERMISSIONS config.
 *
 * Since users may already have URLs from before this feature was released, and the config could theoretically
 * be turned on and off again, users might pass around URLs that don't match the current expected state.
 *
 * If PERMISSIONS_ENABLE_GRANULAR_FOLDER_PERMISSIONS is turned on:
 * - Ensure that both SOURCE and FOLDER are in the types filter
 * - Ensure that TOP_LEVEL_PERMISSIONS is present
 *
 * If PERMISSIONS_ENABLE_GRANULAR_FOLDER_PERMISSIONS is turned off:
 * - Remove FOLDER from the types filter if present
 * - Remove the TOP_LEVEL_PERMISSIONS filter if present
 */
export function sanitizeProjectParamsForGranularPermissions(searchParams: SearchParams): SearchParams {
  const typesFilter = FilterUtil.getFilterByKey(searchParams.filters, FilterKey.TYPES) as MultiValueFilter;
  const topLevelPermissionsFilter = FilterUtil.getFilterByKey(
    searchParams.filters,
    FilterKey.TOP_LEVEL_PERMISSIONS
  );
  const expectedTypes: Array<string> = [ModelTypeConstants.SOURCE];
  let newFilters = [...searchParams.filters];
  if (BenchlingConfig.PERMISSIONS_ENABLE_GRANULAR_FOLDER_PERMISSIONS) {
    expectedTypes.push(ModelTypeConstants.FOLDER);
    if (!topLevelPermissionsFilter) {
      newFilters.push({
        ...keyToEmptyFilter(FilterKey.TOP_LEVEL_PERMISSIONS),
        value: 'true',
      });
    }
  } else {
    // If granular permissions is disabled, make sure to filter out any TOP_LEVEL_PERMISSIONS filter that
    // came from the URL since the ACEs might not exist to properly match based on that.
    newFilters = newFilters.filter((f) => f.key !== FilterKey.TOP_LEVEL_PERMISSIONS);
  }

  if (
    !typesFilter ||
    typesFilter.value.length !== expectedTypes.length ||
    !typesFilter.value.every((t) => expectedTypes.includes(t))
  ) {
    newFilters = FilterUtil.replaceByKey(newFilters, {
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: expectedTypes,
    });
  }

  return {
    ...searchParams,
    filters: newFilters,
  };
}
